import { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { Environment } from '@react-three/drei'
import { motion, AnimatePresence } from 'framer-motion'
import { HelmetControlPanel } from './components/HelmetControlPanel'
import { Hero } from './components/Hero'
import { Presentation } from './components/Presentation'
import { Contact } from './components/Contact'
import { LoadingScreen } from './components/LoadingScreen'
import { Scene3D } from './components/Scene3D'
import { Highlights } from './components/Highlights'
import { CustomCursor } from './components/CustomCursor'
import { ProjectDUI } from './components/ProjectDUI'
import { ProjectTypography } from './components/ProjectTypography'
import { ProjectBeigua } from './components/ProjectBeigua'
import { ProjectSkatepark } from './components/ProjectSkatepark'
import { ProjectLavazza } from './components/ProjectLavazza'
import { ProjectLavazzaBilanci } from './components/ProjectLavazzaBilanci'
import { ProjectJuventus } from './components/ProjectJuventus'
import { ProjectOVE } from './components/ProjectOVE'
import { ProjectFPT } from './components/ProjectFPT'
import { ProjectDISTINTO } from './components/ProjectDISTINTO'
import { ProjectPhoto } from './components/ProjectPhoto'
import { ProjectDaftPunk } from './components/ProjectDaftPunk'
import { GenericProject } from './components/GenericProject'
import { Works } from './components/Works'

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [helmetColor, setHelmetColor] = useState('#1a1a1a')
  const [helmetSize, setHelmetSize] = useState(3.1875)
  const [showHelmet, setShowHelmet] = useState(true)
  const [currentPage, setCurrentPage] = useState('home')
  const [isMobile, setIsMobile] = useState(false)

  // Rileva se siamo su mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768 || 
        ('ontouchstart' in window) || 
        (navigator.maxTouchPoints > 0)
      setIsMobile(mobile)
      // Su mobile: helmet 50% piu piccolo
      setHelmetSize(mobile ? 1.59375 : 3.1875)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setShowHelmet(window.scrollY <= 100)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1)
      
      const projectPages = [
        'dui', 'juventus', 'ove', 'fpt', 
        'distinto', 'photo', 'beigua-trophy', 'genova-pra-skatepark',
        'typography-architecture', 'lavazza-professional', 'lavazza-bilanci',
        'tribute-discovery-daft-punk'
      ]
      
      if (projectPages.includes(hash)) {
        setCurrentPage(hash)
        setShowHelmet(false)
        window.scrollTo(0, 0)
      } else {
        setCurrentPage('home')
        setShowHelmet(true)
        window.scrollTo(0, 0)
      }
    }
    
    handleHashChange()
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Project files mapping
  const projectFiles: Record<string, string[]> = {
  }

  // Show generic project pages for others
  if (projectFiles[currentPage]) {
    return (
      <>
        <CustomCursor />
        <GenericProject />
      </>
    )
  }

  // Show specific project pages
  const projectPages: Record<string, JSX.Element> = {
    dui: <ProjectDUI />,
    juventus: <ProjectJuventus />,
    ove: <ProjectOVE />,
    fpt: <ProjectFPT />,
    distinto: <ProjectDISTINTO />,
    photo: <ProjectPhoto />,
    'beigua-trophy': <ProjectBeigua />,
    'genova-pra-skatepark': <ProjectSkatepark />,
    'typography-architecture': <ProjectTypography />,
    'lavazza-professional': <ProjectLavazza />,
    'lavazza-bilanci': <ProjectLavazzaBilanci />,
    'tribute-discovery-daft-punk': <ProjectDaftPunk />,
  }

  if (projectPages[currentPage]) {
    return (
      <>
        <CustomCursor />
        {projectPages[currentPage]}
      </>
    )
  }

  return (
    <div className="relative min-h-screen bg-black">
      <CustomCursor />

      {/* 3D Canvas - Desktop only */}
      {!isMobile && (
        <motion.div 
          className="fixed inset-0 z-0"
          initial={{ opacity: 1 }}
          animate={{ opacity: showHelmet ? 1 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          <Canvas 
            camera={{ position: [0, 0, 10], fov: 75 }}
            dpr={[1, 2]}
            gl={{ powerPreference: 'high-performance' }}
          >
            <Scene3D helmetColor={helmetColor} helmetSize={helmetSize} />
            <Environment preset="studio" />
          </Canvas>
        </motion.div>
      )}

      {/* Mobile: Video fallback instead of 3D canvas */}
      {isMobile && (
        <motion.div 
          className="fixed inset-0 z-0 flex items-center justify-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: showHelmet ? 1 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{ backgroundColor: '#000000' }}
        >
          <video 
            src="/assets/projects/tribute-discovery-daft-punk/wip_daft.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-contain"
          />
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {isLoading && <LoadingScreen />}
      </AnimatePresence>

      <div className="relative z-10">
        {/* HelmetControlPanel - Desktop only */}
        {!isMobile && (
          <HelmetControlPanel 
            onColorChange={setHelmetColor}
            onSizeChange={(size) => setHelmetSize(size * 3.1875)}
            onRotationChange={() => {}}
          />
        )}

        <main>
          <Hero />
          <Presentation isMobile={isMobile} />
          <Highlights isMobile={isMobile} />
          <Contact isMobile={isMobile} />
        </main>
      </div>
    </div>
  )
}

export default App
